import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PACKAGES, gbp, fmtDate } from "@/lib/constants";
import { StatusBadge, EmptyState } from "@/components/ui/bits";
import { TaglineDivider } from "@/components/Brand";
import { CalendarClock, FileText, MessageSquare, Inbox, ArrowRight, CheckCircle2 } from "lucide-react";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export default function ClientHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClient(data.filter(Boolean)[0] || null)).catch(() => {});
    api.get("/deadlines").then(({ data }) => setDeadlines(data)).catch(() => {});
    api.get("/info-requests").then(({ data }) => setReqs(data)).catch(() => {});
    api.get("/messages/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
  }, []);

  const openReqs = reqs.filter((r) => r.status === "open");
  const upcoming = deadlines.filter((d) => d.status !== "completed").slice(0, 5);
  const pkg = client ? PACKAGES[client.package] : null;

  return (
    <div className="space-y-7">
      <div>
        <p className="text-slate-400 text-lg">{greeting()},</p>
        <h1 className="font-serif font-bold text-4xl text-white">{user.name}</h1>
      </div>

      {/* Package card */}
      {pkg && (
        <div className="rounded-2xl p-6 bg-gradient-to-b from-[#16223D] to-[#131C31] border border-[#C9A84A]/40 shadow-2xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A84A]">Your package</span>
          <div className="flex items-end justify-between mt-1">
            <h2 className="font-serif font-bold text-white text-3xl">{pkg.name}</h2>
            <span className="gold-text font-serif font-bold text-xl">{client.fee ? `${gbp(client.fee)}/mo` : "Custom"}</span>
          </div>
          <div className="mt-4 space-y-1.5">
            {pkg.features.slice(0, 5).map((ft) => (
              <div key={ft} className="flex items-center gap-2 text-slate-300 text-sm"><CheckCircle2 size={15} className="text-[#C9A84A]" /> {ft}</div>
            ))}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <Quick icon={Inbox} label="Info requests" value={openReqs.length} color="#FCD34D" onClick={() => navigate("/documents")} testid="quick-requests" />
        <Quick icon={MessageSquare} label="Unread messages" value={unread} color="#93C5FD" onClick={() => navigate("/messages")} testid="quick-messages" />
        <Quick icon={FileText} label="Documents" value={null} color="#C9A84A" onClick={() => navigate("/documents")} testid="quick-documents" />
        <Quick icon={CalendarClock} label="Deadlines" value={upcoming.length} color="#6EE7B7" onClick={() => navigate("/account")} testid="quick-deadlines" />
      </div>

      {/* Info requests */}
      {openReqs.length > 0 && (
        <div>
          <h2 className="font-serif font-bold text-xl text-white mb-3">We need a few things</h2>
          <div className="space-y-2">
            {openReqs.map((r) => (
              <div key={r.id} className="card-surface rounded-xl p-4 border-l-2 border-[#FCD34D]">
                <div className="text-white font-medium">{r.title}</div>
                {r.description && <div className="text-slate-400 text-sm mt-0.5">{r.description}</div>}
                <button onClick={() => navigate("/documents")} className="gold-text text-sm mt-2 flex items-center gap-1 hover:underline">Upload now <ArrowRight size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming deadlines */}
      <div>
        <h2 className="font-serif font-bold text-xl text-white mb-3">Upcoming deadlines</h2>
        {upcoming.length === 0 ? <EmptyState icon={CalendarClock} title="Nothing due right now" hint="We'll let you know when something's coming up." /> : (
          <div className="space-y-2">
            {upcoming.map((d) => (
              <div key={d.id} className="card-surface rounded-xl p-4 flex items-center justify-between">
                <div><div className="text-white text-sm">{d.title}</div><div className="text-slate-500 text-xs">Due {fmtDate(d.due_date)}</div></div>
                <StatusBadge status={d.status} overdue={d.is_overdue} />
              </div>
            ))}
          </div>
        )}
      </div>

      <TaglineDivider />
    </div>
  );
}

function Quick({ icon: Icon, label, value, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="card-surface rounded-2xl p-4 text-left hover:bg-[#18243F] transition-colors">
      <Icon size={20} style={{ color }} />
      <div className="flex items-end gap-2 mt-2">
        {value !== null && <span className="font-serif font-bold text-2xl" style={{ color }}>{value}</span>}
        <span className="text-slate-300 text-sm mb-0.5">{label}</span>
      </div>
    </button>
  );
}
